import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useDebounce } from 'ahooks';
import { useActivitySensor } from '@archipro-website/react-hooks';
import {
    USER_ACTIVE_STATUS_API_ROUTE,
    type loader,
} from '~/routes/remix-api.user.active-status';
import { useUserLoggedIn } from '~/modules/user';
import { useUserActiveStatusUpdates } from './use-user-active-status-updates';

/**
 * The active status of a user.
 */
export type UserActiveStatus = 'active' | 'inactive';

/**
 * The cached active status for a user.
 */
interface CachedActiveStatus {
    status: UserActiveStatus;
    lastFetched: number;
}

/**
 * The cache TTL in milliseconds (1 minute).
 */
const CACHE_TTL_MS = 60 * 1000;

/**
 * User status cache by member ID.
 */
const userStatusCacheAtom = atom<Map<number, CachedActiveStatus>>(new Map());

/**
 * The set of member IDs waiting to be fetched. Components using
 * `useUserActiveStatus` add their member IDs here.
 */
const membersToFetchAtom = atom<Set<number>>(new Set<number>());

/**
 * Atom to track which member IDs are currently being fetched.
 */
const fetchingMemberIDsAtom = atom<Set<number>>(new Set<number>());

/**
 * Hook to get the active status for a single member.
 * This hook registers the member ID to be batch-fetched by useLoadUserStatuses.
 *
 * @param memberIDs The member IDs to get the active status for.
 * @returns The active status ('active' | 'inactive') or `undefined` if not yet loaded.
 */
export function useUserActiveStatus(
    memberIDs: number[] | null | undefined
): Record<number, UserActiveStatus | null> {
    const memberIDsString = memberIDs ? JSON.stringify(memberIDs) : '';
    const isMounted = useRef(false);
    const isRegistered = useRef(false);
    const cache = useAtomValue(userStatusCacheAtom);
    const setMembersToFetch = useSetAtom(membersToFetchAtom);
    const fetchingIDs = useAtomValue(fetchingMemberIDsAtom);
    const { user, isLoggedIn } = useUserLoggedIn();

    // Register this member ID to be fetched.
    useEffect(() => {
        if (!memberIDs?.length || isRegistered.current) {
            return;
        }
        const now = Date.now();
        const membersToFetch = memberIDs.filter((id) => {
            if (isLoggedIn && id === user.ID) {
                // Don't fetch the user's own status.
                return false;
            }
            const cached = cache.get(id);
            return !cached || now - cached.lastFetched > CACHE_TTL_MS;
        });
        if (membersToFetch.length > 0) {
            setMembersToFetch((prev) => {
                const next = new Set([...prev, ...membersToFetch]);
                if (next.size !== prev.size) {
                    return next;
                }
                return prev;
            });
        }
        isRegistered.current = true;
    }, [memberIDs, isLoggedIn, user.ID, cache, fetchingIDs, setMembersToFetch]);

    // Recheck the statuses when the member IDs change.
    useEffect(() => {
        if (isMounted.current) {
            isRegistered.current = false;
        } else {
            isMounted.current = true;
        }
    }, [memberIDsString]);

    // Return the cached status.
    return useMemo(() => {
        if (!memberIDs?.length) {
            return {};
        }
        return memberIDs.reduce<Record<number, UserActiveStatus | null>>(
            (acc, memberID) => {
                const cached = cache.get(memberID);
                acc[memberID] = cached?.status ?? null;
                return acc;
            },
            {}
        );
    }, [memberIDs, cache]);
}

/**
 * Hook to batch load user active statuses. This should be called **only once** at
 * the root component (`AppPage`) to handle batch fetching of user active statuses.
 *
 * This hook watches the `membersToFetchAtom` and fetches statuses when:
 * - There are members waiting to be fetched
 * - The fetcher is idle
 *
 * Results are cached with a 1-minute TTL.
 */
export function useBatchLoadUserActiveStatuses() {
    const { isLoggedIn } = useUserLoggedIn();
    const isPageActive = useActivitySensor();
    const [membersToFetch, setMembersToFetch] = useAtom(membersToFetchAtom);
    const [fetchingIDs, setFetchingIDs] = useAtom(fetchingMemberIDsAtom);
    const [cache, setCache] = useAtom(userStatusCacheAtom);
    const fetcher = useFetcher<typeof loader>({ key: 'user-active-status' });
    const processedDataRef = useRef<typeof fetcher.data | null>(null);
    const memberIDsToFetch = useMemo(() => {
        if (!isLoggedIn) {
            return [];
        }
        return Array.from(membersToFetch).filter((id) => !fetchingIDs.has(id));
    }, [membersToFetch, fetchingIDs, isLoggedIn]);
    const memberIDsToFetchDebounced = useDebounce(memberIDsToFetch, {
        wait: 100,
    });
    useUserActiveStatusUpdates({
        onMessage: (message) => {
            if (message.type === 'user/online') {
                setCache((prev) => {
                    const next = new Map(prev);
                    next.set(message.member_id, {
                        status: 'active',
                        lastFetched: Date.now(),
                    });
                    return next;
                });
            } else if (message.type === 'user/offline') {
                setCache((prev) => {
                    const next = new Map(prev);
                    next.set(message.member_id, {
                        status: 'inactive',
                        lastFetched: Date.now(),
                    });
                    return next;
                });
            }
        },
    });

    const fetchStatuses = useCallback(
        (idsToFetch: number[]) => {
            if (
                idsToFetch.length === 0 ||
                fetcher.state !== 'idle' ||
                !isLoggedIn
            ) {
                return;
            }
            // Mark these IDs as being fetched.
            setFetchingIDs(new Set(idsToFetch));
            // Remove from the to-fetch set.
            setMembersToFetch((prev) => {
                const next = new Set(prev);
                for (const id of idsToFetch) {
                    next.delete(id);
                }
                return next;
            });

            const params = new URLSearchParams();
            for (const id of idsToFetch) {
                params.append('memberIDs', String(id));
            }
            fetcher.load(
                `${USER_ACTIVE_STATUS_API_ROUTE}?${params.toString()}`
            );
        },
        [fetcher, isLoggedIn, setFetchingIDs, setMembersToFetch]
    );

    // Trigger fetch with debounce to allow batching.
    useEffect(() => {
        if (
            memberIDsToFetch.length &&
            memberIDsToFetchDebounced.length &&
            fetcher.state === 'idle'
        ) {
            fetchStatuses(memberIDsToFetchDebounced);
        }
    }, [
        memberIDsToFetch,
        memberIDsToFetchDebounced,
        fetcher.state,
        fetchStatuses,
    ]);

    // Set and cache user statuses from fetcher response.
    useEffect(() => {
        if (
            fetcher.data === undefined ||
            fetcher.state !== 'idle' ||
            !isLoggedIn ||
            fetcher.data === processedDataRef.current
        ) {
            return;
        }
        if (fetcher.data) {
            const statuses = fetcher.data.statuses;
            setCache((prev) => {
                const next = new Map(prev);
                for (const [memberID, status] of Object.entries(statuses)) {
                    if (status) {
                        next.set(Number(memberID), {
                            status,
                            lastFetched: Date.now(),
                        });
                    } else {
                        next.delete(Number(memberID));
                    }
                }
                return next;
            });
        }
        // Clear the fetching IDs.
        setFetchingIDs(new Set());
        setMembersToFetch((prev) => {
            const next = new Set(prev);
            for (const id of fetchingIDs) {
                next.delete(Number(id));
            }
            if (next.size !== prev.size) {
                return next;
            }
            return prev;
        });
        processedDataRef.current = fetcher.data;
    }, [
        fetcher.data,
        fetcher.state,
        isLoggedIn,
        fetchingIDs,
        setCache,
        setFetchingIDs,
        setMembersToFetch,
    ]);

    // Periodically check for stale cache entries and re-fetch them.
    useEffect(() => {
        if (!isLoggedIn || !isPageActive) {
            return;
        }
        const intervalId = setInterval(
            () => {
                const now = Date.now();
                const staleIDs: number[] = [];

                for (const [memberID, cached] of cache.entries()) {
                    if (now - cached.lastFetched > CACHE_TTL_MS) {
                        staleIDs.push(memberID);
                    }
                }

                if (staleIDs.length > 0) {
                    setMembersToFetch((prev) => {
                        const next = new Set([...prev, ...staleIDs]);
                        if (next.size !== prev.size) {
                            return next;
                        }
                        return prev;
                    });
                }
            },
            // 5 minutes.
            300_000
        );

        return () => {
            clearInterval(intervalId);
        };
    }, [isLoggedIn, isPageActive, cache, setMembersToFetch]);

    // Clear cache on logout.
    useEffect(() => {
        if (!isLoggedIn) {
            setCache(new Map());
            setFetchingIDs(new Set());
            setMembersToFetch(new Set());
        }
    }, [isLoggedIn, setCache, setFetchingIDs, setMembersToFetch]);

    return {
        /**
         * The cached statuses.
         */
        statuses: cache,
        /**
         * The statuses are currently being fetched.
         */
        isLoading: fetcher.state !== 'idle',
    };
}
